import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import AuthService from '../../../shared/services/authService';
import { setUser, setAuthenticated } from '../../../core/store/slices/userSlice';
import type { User } from '../../../shared/types';

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
  onSuccess: (user: User) => void;
}

const SignupModal: React.FC<SignupModalProps> = ({
  isOpen,
  onClose,
  onSwitchToLogin,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    invitation_code: '',
    password: '',
    confirmPassword: '',
    terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { t } = usePageTranslation('public', 'SignupModal');
  const dispatch = useDispatch();

  // Fermer avec la touche Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Empêcher le scroll du body quand la modale est ouverte
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation (comme dans l'ancien code)
    if (!formData.username || !formData.email || !formData.invitation_code || !formData.password || !formData.confirmPassword || !formData.terms) {
      setError(t('error.required'));
      setLoading(false);
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      setError('Le nom d\'utilisateur ne peut contenir que des lettres, chiffres, tirets et underscores (pas d\'espaces).');
      setLoading(false);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError(t('error.emailInvalid'));
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('error.passwordMismatch'));
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t('error.passwordTooShort'));
      setLoading(false);
      return;
    }

    try {
      const authService = AuthService.getInstance();
      const response = await authService.signup({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        invitation_code: formData.invitation_code,
      });
      
      if (response.success && response.user) {
        // Adapter l'utilisateur au format attendu par Redux
        const userForRedux = {
          ...response.user,
          activated: (response.user as any).activated !== undefined ? (response.user as any).activated : true,
          bann: 0,
          premium: false,
          roles_id: (response.user as any).roles_id || 18, // Utiliser le roles_id de l'API ou 18 (Joueur) par défaut
        };
        
        // Stocker l'utilisateur dans Redux
        dispatch(setUser(userForRedux as any));
        dispatch(setAuthenticated(true));
        
        // Inscription réussie
        onSuccess(userForRedux as User);
        onClose();
        
        // Réinitialiser le formulaire
        setFormData({
          username: '',
          email: '',
          invitation_code: '',
          password: '',
          confirmPassword: '',
          terms: false,
        });
      } else {
        setError(response.error || t('error.generic'));
      }
    } catch (err) {
      setError(t('error.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-gray-800/95 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border-2 border-gray-700/50">
        {/* Header avec logo et titre */}
        <div className="bg-gradient-to-r from-secondary-500 via-secondary-600 to-primary-500 text-white p-6 rounded-t-xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src={`${process.env.PUBLIC_URL}/images/logo.png`} 
                alt="OpSecours" 
                className="w-8 h-8 mr-3"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <h2 className="text-2xl font-bold">{t('title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-white bg-red-600 hover:bg-red-500 rounded-lg p-2 transition-all duration-200 hover:scale-110 border-2 border-white shadow-lg flex items-center justify-center"
              aria-label="Fermer"
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="text-white"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-red-900/50 border-2 border-red-500/50 text-red-200 px-4 py-3 rounded mb-4 notification-game">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                {t('username')}
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                autoComplete="username"
                className="input-game w-full"
                placeholder={t('usernamePlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-500">Lettres, chiffres, - et _ uniquement (pas d'espaces)</p>
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                {t('email')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                className="input-game w-full"
                placeholder={t('emailPlaceholder')}
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="invitation_code" className="block text-sm font-medium text-gray-300 mb-2">
              {t('invitationCode')}
            </label>
            <input
              type="text"
              id="invitation_code"
              name="invitation_code"
              value={formData.invitation_code}
              onChange={handleChange}
              required
              autoComplete="off"
              className="input-game w-full"
              placeholder={t('invitationCodePlaceholder')}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              {t('password')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                className="input-game w-full pr-12"
                placeholder={t('passwordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
              {t('confirmPassword')}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                className="input-game w-full pr-12"
                placeholder={t('confirmPasswordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                aria-label={showConfirmPassword ? 'Masquer la confirmation du mot de passe' : 'Afficher la confirmation du mot de passe'}
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="terms"
                checked={formData.terms}
                onChange={(e) => setFormData({ ...formData, terms: e.target.checked })}
                required
                className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-600 bg-gray-700 rounded"
              />
              <span className="text-sm text-gray-300">
                {t('terms')}{' '}
                <a href="/legal/conditions-utilisation" className="text-primary-400 hover:text-primary-300 underline">
                  {t('termsLink')}
                </a>
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-game w-full flex items-center justify-center"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">{t('submit')}...</span>
              </>
            ) : (
              <>
                <i className="bx bx-user-plus mr-2"></i>
                {t('submit')}
              </>
            )}
          </button>

          <div className="mt-4 text-center">
            <p className="text-gray-400">
              {t('switch')}{' '}
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="text-primary-400 hover:text-primary-300 font-semibold transition-colors"
              >
                {t('switchLink')}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupModal;
